import { useEffect, useState, useRef } from "react";
import { useIntl } from "react-intl";
import { useSelector, useDispatch } from "react-redux";
import { Modal, ModalBody, ModalFooter, Button, Input, Form, Select } from "@gpstrackit/react-controls";
import { NotificationManager } from 'Components/Notifications';
import { Grid, Switch } from "@mui/material";

//Actions 
import {
    clearRedux,
    cannedCommandCreateRequest,
    cannedCommandUpdateRequest,
    cannedCommandGetRequest,
} from 'Redux/actions';

const CreateEditCannedCommand = (props) => {
    const { manufacturer, tableRef } = props;
    const { messages } = useIntl();
    const formRef = useRef();
    const dispatch = useDispatch();

    //Local States
    const [id, setId] = useState(null)
    const [label, setLabel] = useState("");
    const [command, setCommand] = useState("");
    const [via, setVia] = useState("SMS");
    const [status, setStatus] = useState({ id: "Active", name: messages['create_canned_command_create_active'] });
    const [unitType, setUnitType] = useState({ id: 'all', name: 'All Unit Types' });
    const [deviceModel, setDeviceModel] = useState({ id: 'all', name: 'All Device Models' });

    //Redux States
    const { openModal, loading, create, update, current, cannedCommand } = useSelector(state => state.createEditCannedCommandRedux);
    const unitTypes = useSelector(state => state.generalRedux.unitTypes);
    const deviceModels = useSelector(state => state.generalRedux.deviceModels);

    const statusOptions = [
        {
            id: "Active",
            name: messages['create_canned_command_create_active']
        },
        {
            id: "Inactive",
            name: messages['create_canned_command_create_inactive']
        }
    ]

    const unitTypeOptions = [
        { id: 'all', name: 'All Unit Types' },
        ...(unitTypes ? unitTypes.filter((item) => item?.manufacturerId === manufacturer?.id) : [])
    ];

    const deviceModelOptions = [
        { id: 'all', name: 'All Device Models' },
        ...(deviceModels ? deviceModels.filter((item) => item?.unitTypeId === unitType?.id) : [])
    ];

    const closeModal = () => {
        dispatch(clearRedux("CREATE_EDIT_CANNEDCOMMAND"));
        setLabel("");
        setCommand("");
        setVia("SMS");
        setStatus({ id: "Active", name: messages['create_canned_command_create_active'] });
        setId(null);
        setUnitType({ id: 'all', name: 'All Unit Types' });
        setDeviceModel({ id: 'all', name: 'All Device Models' });
    }

    const applyChanges = () => {
        let payload = {
            label: label,
            command: command,
            via: via,
            manufacturerId: manufacturer.id,
            unitTypeId: unitType.id === 'all' ? null : unitType.id,
            deviceModelId: deviceModel.id === 'all' ? null : deviceModel.id,
        }
        if (id) {
            payload.id = id;
            payload.status = status.id;
            dispatch(cannedCommandUpdateRequest(payload))
        } else dispatch(cannedCommandCreateRequest(payload));
    }

    const handleUnitTypeChange = (value) => {
        setUnitType(value || { id: 'all', name: 'All Unit Types' });
        setDeviceModel({ id: 'all', name: 'All Device Models' });
    };

    useEffect(() => {
        if (openModal && current) {
            dispatch(cannedCommandGetRequest(current?.id))
        }
    }, [openModal])

    useEffect(() => {
        if (create?.id) {
            closeModal();
            tableRef && tableRef?.current?.getData();
            dispatch(clearRedux("CREATE_EDIT_CANNEDCOMMAND", { create: null }));
            NotificationManager.success(messages['create_canned_command_create_success_message'], messages['create_canned_command_create_title'], 3000);
        }
        if (update?.id) {
            closeModal();
            tableRef && tableRef?.current?.getData();
            dispatch(clearRedux("CREATE_EDIT_CANNEDCOMMAND", { update: null }));
            NotificationManager.success(messages['create_canned_command_update_success_message'], messages['create_canned_command_edit_title'], 3000);
        }
        if (cannedCommand) {
            dispatch(clearRedux("CREATE_EDIT_CANNEDCOMMAND", { cannedCommand: null }))
            cannedCommand?.label && setLabel(cannedCommand?.label);
            cannedCommand?.command && setCommand(cannedCommand?.command);
            cannedCommand?.via && setVia(cannedCommand.via);
            cannedCommand?.status && setStatus(statusOptions.find(x => x.id === cannedCommand?.status));
            cannedCommand?.id && setId(cannedCommand?.id);

            if (cannedCommand?.unitType?.id) {
                setUnitType({ id: cannedCommand.unitType.id, name: cannedCommand.unitType.name });
            }
            if (cannedCommand?.deviceModel) {
                setDeviceModel({ id: cannedCommand.deviceModel.id, name: cannedCommand.deviceModel.name });
            }
        }
    }, [create, update, cannedCommand])

    return (
        <Modal
            open={openModal}
            title={current ? messages['create_canned_command_edit_title'] : messages['create_canned_command_create_title']}
            handleClose={() => closeModal()}
            position="right"
            loading={loading}
            size="small"
        >
            <ModalBody>
                <div style={{ padding: '0 10px' }}>
                    <Form
                        ref={formRef}
                        onValidSubmit={() => applyChanges()}>
                        <div className="manufacturer-input">
                            <Input
                                label={messages['create_canned_command_manufacturer']}
                                placeholder={messages['create_canned_command_manufacturer']}
                                value={manufacturer?.name || ""}
                                onChange={() => { }}
                                disabled
                            />
                        </div>
                        <div style={{ marginTop: 5 }}>
                            <Input
                                label={messages['create_canned_command_label']}
                                placeholder={messages['create_canned_command_label']}
                                value={label}
                                onChange={(value) => setLabel(value)}
                                maxLength={50}
                                required
                                requiredMessage={messages["common_field_required"]}
                            />
                        </div>
                        <div style={{ marginTop: 5 }}>
                            <Input
                                type="textarea"
                                label={messages['create_canned_command_command']}
                                placeholder={messages['create_canned_command_command']}
                                value={command}
                                onChange={(value) => setCommand(value)}
                                required
                                maxLength={1000}
                                requiredMessage={messages["common_field_required"]}
                            />
                        </div>
                        <div style={{ marginTop: 15, marginBottom: 10 }}>
                            <Grid item xs={12}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <label className="MuiTypography-root MuiFormControlLabel-label MuiTypography-body1" style={{ marginRight: 15 }}>
                                        {messages['create_canned_command_via'] || "Via:"}
                                    </label>
                                    <span style={{ fontWeight: via === 'SMS' ? 'bold' : 'normal' }}>SMS</span>
                                    <Switch
                                        checked={via === 'OTA'}
                                        onChange={(e) => setVia(e.target.checked ? 'OTA' : 'SMS')}
                                        color="primary"
                                    />
                                    <span style={{ fontWeight: via === 'OTA' ? 'bold' : 'normal' }}>OTA</span>
                                </div>
                            </Grid>
                        </div>
                        <div style={{ marginTop: 20, marginBottom: 5 }}>
                            <label className="MuiTypography-root MuiFormControlLabel-label MuiTypography-body1" style={{ fontWeight: 'bold' }}>
                                This command is available for:
                            </label>
                        </div>
                        <div style={{ marginTop: 5 }}>
                            <Select
                                id="unit-type-list"
                                label={messages['create_canned_command_unit_type'] || "Unit Type"}
                                placeholder="Select Unit Type"
                                options={unitTypeOptions}
                                onChange={handleUnitTypeChange}
                                value={unitType}
                                required
                                textField="name"
                                valueField="id"
                                isClearable={false}
                            />
                        </div>
                        {unitType && unitType.id !== 'all' && (
                            <div style={{ marginTop: 5 }}>
                                <Select
                                    id="device-model-list"
                                    label="Device Model"
                                    placeholder="Select Device Model"
                                    options={deviceModelOptions}
                                    onChange={(value) => setDeviceModel(value || { id: 'all', name: 'All Device Models' })}
                                    value={deviceModel}
                                    required
                                    textField="name"
                                    valueField="id"
                                    isClearable={false}
                                />
                            </div>
                        )}
                        {id && (
                            <div style={{ marginTop: 5 }}>
                                <Select
                                    id="change-status-id"
                                    valueField="id"
                                    textField="name"
                                    value={status}
                                    label={messages['account_change_status_label']}
                                    placeholder={messages['account_change_status_label']}
                                    options={statusOptions}
                                    onChange={(value) => setStatus(value)}
                                    required
                                    requiredMessage={messages['required_field']}
                                />
                            </div>
                        )}
                    </Form>
                </div>
            </ModalBody>
            <ModalFooter>
                <Button className="btn-cancel" onClick={() => closeModal()}>
                    {messages['cancel']}
                </Button>
                <Button className="btn-dark-blue" onClick={() => formRef?.current?.submit()}>
                    {messages['save']}
                </Button>
            </ModalFooter>
        </Modal>
    )
}

export default CreateEditCannedCommand;